/**
 * Conflict-Resolver — sichtbar wenn card.state = pending_conflict.
 *
 * Ist in Phase 2 (wo der Konflikt entsteht) UND Phase 7 (parallel) eingebettet.
 * Der User muss nicht zwischen Phasen springen — die Resolve-Option ist da, wo
 * der Konflikt sichtbar wird.
 *
 * Drei Aktionen, mappen direkt auf hub/src/routes/timesheets.ts POST /:id/resolve-conflict:
 *   overwrite  — diese Karte ersetzt die existierende (existing wird superseded)
 *   discard    — diese Karte verwerfen (state → archived)
 *   reassign   — anderen Mitarbeiter zuordnen (state → ocr_complete mit neuem employee_id)
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type Card, type Employee, type Timesheet } from "../../lib/api";

interface ConflictResolverProps {
  card: Card;
  timesheet: Timesheet | null;
  employees: Employee[];
  onResolved: () => void;
}

export function ConflictResolver({ card, timesheet, employees, onResolved }: ConflictResolverProps) {
  const [busy, setBusy] = useState<"overwrite" | "discard" | "reassign" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reassignEmpId, setReassignEmpId] = useState<string>("");

  if (card.state !== "pending_conflict") return null;

  const conflict = (timesheet?.data as any)?.conflict ?? {};
  const existingCardId = conflict.existing_card_id ?? null;
  const existingState = conflict.existing_state ?? "?";
  const reason = conflict.reason ?? "?";
  const detectedBy = conflict.detected_by ?? "?";

  async function run(action: "overwrite" | "discard" | "reassign") {
    setErr(null);
    setBusy(action);
    try {
      const empId = action === "reassign" ? reassignEmpId : undefined;
      if (action === "reassign" && !empId) {
        throw new Error("Bitte einen Mitarbeiter auswählen für reassign");
      }
      await api.timesheets.resolveConflict(card.id, action, empId);
      onResolved();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="conflict-resolver" style={{
      marginBottom: 14,
      padding: 14,
      background: "#fee2e2",
      border: "1px solid #fca5a5",
      borderRadius: 6,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 14, color: "#991b1b" }}>🔴 Konflikt: pending_conflict</strong>
        <span style={{ fontSize: 11, color: "#7f1d1d" }}>
          erkannt {detectedBy === "pre_state_check" ? "von Worker pre-check" : detectedBy}
        </span>
      </div>

      <div style={{ marginBottom: 10, fontSize: 13, color: "#374151" }}>
        <strong>Grund:</strong> {reason}
        {reason === "employee_month_duplicate" && (
          <> {" — "}für (Mitarbeiter × Monat) existiert bereits eine andere aktive Karte.</>
        )}
        <br />
        {existingCardId && (
          <>
            <strong>Existierende Karte:</strong>{" "}
            <Link to={`/timesheets/${existingCardId}`} style={{ fontFamily: "monospace" }}>
              {existingCardId.slice(0, 8)}
            </Link>
            {" "}<span style={{ color: "#6b7280" }}>(state={existingState})</span>
          </>
        )}
      </div>

      <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr", maxWidth: 700 }}>
        {/* OVERWRITE */}
        <div style={{ padding: 10, background: "white", border: "1px solid #e5e7eb", borderRadius: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>↻ Diese Karte behalten (overwrite)</strong>
            <button
              type="button"
              onClick={() => run("overwrite")}
              disabled={!!busy || !existingCardId}
              style={{ marginLeft: "auto", padding: "4px 12px", fontSize: 12, fontWeight: 600,
                background: "#1d4ed8", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              {busy === "overwrite" ? "…" : "Overwrite"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Diese Karte ({card.id.slice(0, 8)}) wird zur aktiven Karte für den Mitarbeiter × Monat.
            Die existierende Karte ({existingCardId?.slice(0, 8) ?? "?"}) wird auf state=superseded gesetzt
            (DSGVO-konform aufbewahrt, aber nicht mehr aktiv).
          </div>
        </div>

        {/* DISCARD */}
        <div style={{ padding: 10, background: "white", border: "1px solid #e5e7eb", borderRadius: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>🗑 Diese Karte verwerfen (discard)</strong>
            <button
              type="button"
              onClick={() => {
                if (confirm("Wirklich verwerfen? Diese Karte geht in state=archived.")) run("discard");
              }}
              disabled={!!busy}
              style={{ marginLeft: "auto", padding: "4px 12px", fontSize: 12, fontWeight: 600,
                background: "#dc2626", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              {busy === "discard" ? "…" : "Discard"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Diese Karte wird auf state=archived gesetzt. Die existierende bleibt aktiv. Wähle das,
            wenn das PDF ein Duplikat war (z.B. zweimal hochgeladen).
          </div>
        </div>

        {/* REASSIGN */}
        <div style={{ padding: 10, background: "white", border: "1px solid #e5e7eb", borderRadius: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>↪ Anderem Mitarbeiter zuordnen (reassign)</strong>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            Wähle einen anderen Mitarbeiter aus. Falls die Header-OCR falsch lag und das PDF
            eigentlich zu einer anderen Person gehört. State wird auf ocr_complete gesetzt.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={reassignEmpId}
              onChange={(e) => setReassignEmpId(e.target.value)}
              style={{ flex: 1, padding: "4px 8px", fontSize: 12 }}
              disabled={!!busy}
            >
              <option value="">— anderen Mitarbeiter wählen —</option>
              {employees
                .filter(e => e.id !== card.employee_id && e.work_schedule)
                .map(e => (
                  <option key={e.id} value={e.id}>
                    {e.last_name}, {e.first_name} ({e.dienstnehmernummer})
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() => run("reassign")}
              disabled={!!busy || !reassignEmpId}
              style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600,
                background: "#0ea5e9", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
            >
              {busy === "reassign" ? "…" : "Reassign"}
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div style={{ marginTop: 10, padding: 8, background: "#fff5f5", border: "1px solid #fca5a5",
          borderRadius: 4, color: "#991b1b", fontSize: 12 }}>
          Fehler: {err}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "#7f1d1d" }}>
        API: POST /api/timesheets/{card.id.slice(0, 8)}…/resolve-conflict · Source: hub/src/routes/timesheets.ts:178
      </div>
    </div>
  );
}
